import { blocked } from 'flare-chat-core/contracts/chat/operation-result';
import { buildComposerExecutionOptions } from './execution-profile.js';
import {
  collectReadyProjectSourceRefs,
  collectSourceIdStrings,
  hasBlockingComposerAttachments,
  mergeSourceReferenceLists,
} from './attachment-send-refs.js';
import { resolveSourceGroundingPolicy } from './source-grounding-policy.js';
import {
  resolveOutboundContent,
  resolveSendAttachmentPayloadRefs,
  resolveSourceGroundingMessage,
  resolveSourcingPayloadExtra,
} from './context-input.js';

function normalizeRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value;
}

function firstTruthyTextValue(...values) {
  const value = values.find(Boolean);
  return String(value || '').trim();
}

function resolveSendContentState(options, composer) {
  const currentInput = String(composer?.value || '').trim();
  const outboundContent = resolveOutboundContent({
    optionsContent: options?.content,
    currentInput,
  });
  return {
    outboundContent,
    sourceGroundingMessage: resolveSourceGroundingMessage({
      outboundContent,
      currentInput,
    }),
  };
}

function resolveSendModeState({
  composerModeKey,
  readyProjectSourceRefs,
  sourceGroundingMessage,
  streamOptions,
}) {
  const executionOptions = buildComposerExecutionOptions(
    firstTruthyTextValue(streamOptions.executionProfile, streamOptions.modeKey, composerModeKey),
  );
  const requestedModeKey = executionOptions.modeKey;
  const sourceGroundingPolicy = resolveSourceGroundingPolicy({
    message: sourceGroundingMessage,
    readySourceRefs: readyProjectSourceRefs,
    // Execution profiles do not select or suppress a source-grounding product path.
    requestedModeKey: '',
  });
  const normalizedModeKey = requestedModeKey;
  Object.assign(streamOptions, executionOptions);
  delete streamOptions.manualModeKey;
  return { normalizedModeKey, sourceGroundingPolicy };
}

function resolveCurrentComposerFileList(composerFileListRef) {
  return Array.isArray(composerFileListRef?.current) ? composerFileListRef.current : [];
}

function isAttachmentSendBlocked(currentComposerFileList, hasNonReadyAttachments) {
  return hasBlockingComposerAttachments(currentComposerFileList)
    || (!currentComposerFileList.length && hasNonReadyAttachments);
}

function resolveMergedSendRefs(options, readyProjectSourceRefs, contextAttachmentRefs) {
  return {
    contextRefs: mergeSourceReferenceLists(
      options?.context_refs,
      options?.knowledge_refs,
      readyProjectSourceRefs,
      contextAttachmentRefs,
    ),
    knowledgeRefIds: collectSourceIdStrings(
      options?.context_refs,
      options?.knowledge_refs,
      readyProjectSourceRefs,
      contextAttachmentRefs,
    ),
  };
}

function applySourceGroundingPayload(streamOptions, sourceGroundingPolicy) {
  if (!sourceGroundingPolicy.active) {
    return;
  }
  delete streamOptions.payloadExtra.mode;
  delete streamOptions.payloadExtra.manual_mode;
  delete streamOptions.payloadExtra.function_type;
  delete streamOptions.payloadExtra.current_mode;
  streamOptions.payloadExtra.source_grounding = sourceGroundingPolicy;
}

export default async function sendWithContextRefs(options, dependencies) {
  const {
    activeArtifactTarget,
    activeWorkspaceTab,
    composer,
    composerFileListRef,
    composerModeKey,
    hasNonReadyAttachments,
    latestSourcingQuery,
    latestSourcingResultIds,
    readyAttachmentRefs,
    savedIntakeCanvasContent,
    setActiveWorkspaceTab,
    sourceItems,
  } = dependencies;
  const streamOptions = { ...normalizeRecord(options?.streamOptions) };
  const sendContentState = resolveSendContentState(options, composer);
  const readyProjectSourceRefs = collectReadyProjectSourceRefs(sourceItems);
  const sendModeState = resolveSendModeState({
    composerModeKey,
    readyProjectSourceRefs,
    sourceGroundingMessage: sendContentState.sourceGroundingMessage,
    streamOptions,
  });
  const currentComposerFileList = resolveCurrentComposerFileList(composerFileListRef);
  if (isAttachmentSendBlocked(currentComposerFileList, hasNonReadyAttachments)) {
    return blocked('attachments_not_ready');
  }
  const attachmentPayload = resolveSendAttachmentPayloadRefs({
    currentComposerFileList,
    readyAttachmentRefs,
    includeSourceLifecycleRefs: true,
  });
  const mergedRefs = resolveMergedSendRefs(
    options,
    readyProjectSourceRefs,
    attachmentPayload.contextAttachmentRefs,
  );
  streamOptions.attachments = attachmentPayload.streamAttachmentRefs;
  streamOptions.payloadExtra = resolveSourcingPayloadExtra({
    streamOptions,
    activeArtifactTarget,
    isSourcingMode: false,
    currentInput: sendContentState.sourceGroundingMessage,
    latestSourcingQuery,
    latestSourcingResultIds,
    normalizedModeKey: sendModeState.normalizedModeKey,
    savedIntakeCanvasContent,
  });
  applySourceGroundingPayload(streamOptions, sendModeState.sourceGroundingPolicy);
  const sendResult = await composer.onSend({
    ...options,
    ...(sendContentState.outboundContent ? { content: sendContentState.outboundContent } : {}),
    streamOptions,
    context_refs: mergedRefs.contextRefs,
    knowledge_refs: mergedRefs.knowledgeRefIds,
    attachedSessionFilesSnapshot: attachmentPayload.sendableAttachmentSnapshot,
  });
  if (sendResult?.status === 'completed' && activeWorkspaceTab === 'sources') {
    setActiveWorkspaceTab('chats');
  }
  return sendResult;
}
